import { LitElement, html, css } from 'lit';
import '#shared/ui/game-status.js';

export interface PdaMessage {
  timestamp: string;
  level: 1 | 2 | 3;
  message: string;
}

export class PdaLog extends LitElement {
  static styles = css`
    :host {
      display: block;
      position: relative;
      z-index: 30;
      padding: 1rem;
      background-color: rgba(22, 22, 20, 0.95); /* bg-card/95 */
      border-top: 1px solid var(--border, #3f3f3c);
      backdrop-filter: blur(24px); /* backdrop-blur-xl */
      font-family: var(--font-mono, monospace);
      overflow: hidden;
      box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.5);
    }

    @media (min-width: 768px) {
      :host {
        padding: 1.5rem;
      }
    }

    :host::before {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: linear-gradient(180deg, rgba(210, 197, 168, 0.03) 0%, transparent 100%);
      z-index: 1;
    }

    .container {
      position: relative;
      z-index: 2;
      display: grid;
      grid-template-cols: 1fr;
      gap: 1rem;
      align-items: start;
    }

    @media (min-width: 1024px) {
      .container {
        grid-template-cols: repeat(4, 1fr);
        gap: 1.5rem;
      }
    }

    .messages-column {
      grid-column: span 1;
    }

    @media (min-width: 1024px) {
      .messages-column {
        grid-column: span 3;
      }
    }

    .status-column {
      display: flex;
      flex-direction: row;
      height: 100%;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
    }

    @media (min-width: 1024px) {
      .status-column {
        flex-direction: column;
        align-items: flex-start;
      }
    }

    .status-wrapper {
      flex: 1;
    }

    @media (min-width: 1024px) {
      .status-wrapper {
        flex: none;
        width: 100%;
      }
    }

    .pda-header {
      font-size: 8px;
      font-weight: 900;
      color: var(--muted-foreground, #a1a1aa);
      text-transform: uppercase;
      letter-spacing: 0.3em;
      margin-bottom: 0.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    @media (min-width: 768px) {
      .pda-header {
        font-size: 9px;
        margin-bottom: 0.75rem;
      }
    }

    .terminal-icon {
      color: var(--accent, #65a30d);
      flex-shrink: 0;
    }

    .message-log {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      overflow-y: auto;
      height: 8rem; /* h-32 */
      background-color: rgba(39, 39, 42, 0.5); /* bg-input/50 */
      border-radius: 0.125rem;
      border: 1px solid var(--border, #3f3f3c);
      padding: 0.75rem;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.5);
      position: relative;
    }

    @media (min-width: 768px) {
      .message-log {
        height: 10rem; /* h-40 */
        padding: 1rem;
        font-size: 11px;
      }
    }

    .message-entry {
      color: var(--muted-foreground, #a1a1aa);
      border-bottom: 1px solid rgba(63, 63, 60, 0.1);
      padding-bottom: 0.25rem;
      animation: slideIn 0.3s ease-out;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateX(-0.5rem);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    .timestamp {
      color: rgba(161, 161, 170, 0.5);
      margin-right: 0.5rem;
      font-size: 8px;
    }

    @media (min-width: 768px) {
      .timestamp {
        font-size: 9px;
      }
    }

    .level-1 { color: rgba(250, 250, 250, 0.8); } /* foreground/80 */
    .level-2 { color: rgba(101, 163, 13, 0.9); } /* accent/90 */
    .level-3 { color: var(--destructive, #ef4444); font-weight: bold; }

    .cursor {
      color: var(--accent, #65a30d);
      animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
      margin-top: 0.25rem;
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: .5; }
    }

    .truncate {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `;

  static properties = {
    messages: { type: Array },
    zone: { type: String },
  };

  declare messages: PdaMessage[];
  declare zone: string;

  constructor() {
    super();
    this.messages = [];
    this.zone = 'SECTOR_0';
  }

  private formatTime(isoString: string) {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString([], {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return '--:--:--';
    }
  }

  render() {
    const messages = [...(this.messages || [])].reverse().slice(0, 50);
    return html`
      <div class="container">
        <div class="messages-column">
          <div class="message-log">
            <div class="cursor">_</div>
            ${messages.map((msg) => html`
              <div class="message-entry">
                <span class="timestamp">[${this.formatTime(msg.timestamp)}]</span>
                <span class="level-${msg.level}">${msg.message}</span>
              </div>
            `)}
          </div>
        </div>

      </div>
    `;
  }
  static register(name = 'pda-log') {
    if(!customElements.get(name))
      customElements.define(name, PdaLog);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pda-log': PdaLog;
  }
  namespace JSX {
    interface IntrinsicElements {
      'pda-log': any;
    }
  }
}
